// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import crest from "../../dmxHelper/assets/wrpfarg-logo.webp";
import type { LiftProgressRow } from "../../live/liftProgress";
import FitLine from "../../live/FitLine";
import type { LabSponsor } from "../labSponsors";
import SponsorRotator from "../../live/SponsorRotator";
import { bestLabel, ClockBox, JudgeLightsBox, recordStyle, totalLabel } from "../../streaming/broadcast/overlayParts";
import type { OverlayView } from "../../streaming/broadcast/overlayView";
import { formatKg, getRecordView, LIFT_LABELS } from "../../streaming/broadcast/overlayView";

import styles from "./OverlayLuces.module.scss";

interface Props {
  view: OverlayView;
  sponsors: ReadonlyArray<LabSponsor>;
}

// Un disco por intento: se enciende blanco (válido) o rojo (nulo), como las luces de los jueces.
const Disc = ({
  status,
  label,
  current = false,
  recordColor,
}: {
  status: "good" | "noLift" | "pending" | "empty" | "bombed";
  label: string;
  current?: boolean;
  recordColor?: string;
}) => (
  <span
    className={styles.disc}
    data-status={status}
    data-current={current ? "true" : undefined}
    data-record={recordColor === undefined ? undefined : "true"}
    style={recordStyle(recordColor)}
  >
    {label}
  </span>
);

const LiftGroup = ({ row, attemptNumber }: { row: LiftProgressRow; attemptNumber: number }) => {
  if (!row.current) {
    const record = getRecordView(row.recordCategory);
    const status = row.state === "bombed" ? "bombed" : row.bestKg === null ? "empty" : "good";
    return (
      <div className={styles.group}>
        <span className={styles.groupLabel}>{LIFT_LABELS[row.lift]}</span>
        <Disc status={status} label={bestLabel(row)} recordColor={record?.color} />
      </div>
    );
  }
  const fourth = row.attempts[3];
  const count = fourth !== undefined && (fourth.weightKg > 0 || fourth.status !== "pending") ? 4 : 3;
  return (
    <div className={styles.group} data-current="true">
      <span className={styles.groupLabel}>{LIFT_LABELS[row.lift]}</span>
      {Array.from({ length: count }, (_, index) => {
        const item = row.attempts[index] ?? { weightKg: 0, status: "pending" as const };
        const empty = item.status === "pending" && item.weightKg <= 0;
        const record = item.recordAttempt === true ? getRecordView(item.recordCategory) : null;
        return (
          <Disc
            key={index}
            status={empty ? "empty" : item.status}
            label={empty ? "—" : formatKg(item.weightKg)}
            current={index === attemptNumber - 1}
            recordColor={record?.color}
          />
        );
      })}
    </div>
  );
};

const OverlayLuces = ({ view, sponsors }: Props) => {
  const athlete = view.athlete;
  return (
    <div className={styles.root}>
      <header className={styles.topBar}>
        <img className={styles.crest} src={crest} alt="WRPF Argentina" />
        <div className={styles.tournament}>
          <strong>{view.tournamentName}</strong>
          <span>{view.sessionLabel}</span>
        </div>
        <SponsorRotator
          sponsors={sponsors}
          perPage={5}
          intervalMs={7000}
          className={styles.sponsors}
          tileClassName={styles.sponsorTile}
        />
      </header>

      {athlete !== null ? (
        <section key={athlete.entryKey} className={styles.lowerThird} aria-label="Atleta en tarima">
          {athlete.record !== null ? (
            <div className={styles.recordBand} style={recordStyle(athlete.record.color, athlete.record.textColor)}>
              Intento de {athlete.record.label.toLowerCase()}
            </div>
          ) : null}
          <div className={styles.panel}>
            <div className={styles.plateBlock}>
              <span
                className={styles.plate}
                data-result={view.result ?? undefined}
                style={recordStyle(athlete.record?.color)}
                data-record={athlete.record === null ? undefined : "true"}
              >
                <strong>{athlete.weightLabel}</strong>
                <small>kg</small>
              </span>
              <span className={styles.plateCaption}>
                {view.result === "good" ? "Válido" : view.result === "noLift" ? "Nulo" : athlete.attemptLabel}
              </span>
            </div>
            <div className={styles.body}>
              <div className={styles.identity}>
                <div className={styles.name}>
                  <FitLine maxFontSize={56} minFontSize={30}>
                    {athlete.name}
                  </FitLine>
                </div>
                <ul className={styles.details}>
                  {athlete.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </div>
              <div className={styles.board}>
                {athlete.progress.rows.map((row) => (
                  <LiftGroup key={row.lift} row={row} attemptNumber={athlete.attemptNumber} />
                ))}
                {athlete.progress.showTotal ? (
                  <div className={styles.total} data-bombed={athlete.progress.bombed ? "true" : undefined}>
                    <span>{athlete.progress.bombed ? "Sin total" : "Total"}</span>
                    <strong>{totalLabel(athlete.progress)}</strong>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      ) : view.waitingForNext ? (
        <section className={styles.waiting} aria-label="Atleta en tarima">
          Esperando al siguiente atleta
        </section>
      ) : null}

      <JudgeLightsBox lights={view.lights} className={styles.lights} />
      <ClockBox seconds={view.clockSeconds} running={view.clockRunning} className={styles.clock} />
    </div>
  );
};

export default OverlayLuces;
